<template>
    <span ref="root" class="info-tip">
        <!-- An ⓘ button that shows a short note in a bubble: on hover where there is a mouse, on
             tap on touch screens (no hover there, and a tap on a hover-only tip does nothing). Also
             opens on keyboard focus; Escape, a second tap or a tap elsewhere closes it. The bubble
             stays in the DOM while closed so the field's aria-describedby can point at it. -->
        <button type="button"
            class="info-tip-button"
            @mouseenter="onHover(true)"
            @mouseleave="onHover(false)"
            :aria-label="label"
            :aria-expanded="open ? 'true' : 'false'"
            :aria-controls="id"
            @click="togglePinned"
            @focus="open = true"
            @blur="onBlur"
            @keydown.esc="close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.41-1 4.71c-.07.34.03.53.3.53.2 0 .49-.07.69-.25l-.09.42c-.29.35-.92.6-1.46.6-.7 0-1-.42-.81-1.31l.74-3.47c.06-.29 0-.4-.29-.47l-.45-.08.08-.38 2.29-.29zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
            </svg>
        </button>
        <span v-show="open" :id="id" role="tooltip" class="info-tip-bubble">{{ text }}</span>
    </span>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';

defineProps({
    id: { type: String, required: true },
    text: { type: String, required: true },
    label: { type: String, default: 'More information' },
});

const root = ref(null);
const open = ref(false);
// opened by a click or tap: stays open until the next tap, Escape or a tap elsewhere
const pinned = ref(false);

const canHover = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(hover: hover)').matches;

function onHover(entering) {
    // touch screens fire mouseenter on tap; the click handler owns those
    if (!canHover || pinned.value) {
        return;
    }
    open.value = entering;
}

function togglePinned() {
    pinned.value = !pinned.value;
    open.value = pinned.value;
}

function onBlur() {
    if (!pinned.value) {
        open.value = false;
    }
}

function close() {
    pinned.value = false;
    open.value = false;
}

function onDocumentClick(event) {
    if (open.value && root.value && !root.value.contains(event.target)) {
        close();
    }
}

onMounted(() => document.addEventListener('click', onDocumentClick));
onBeforeUnmount(() => document.removeEventListener('click', onDocumentClick));
</script>

<style scoped>
/* No box of its own: the button and the bubble sit in LcField's label row (a wrapping flex row),
   so the bubble can take a line of its own under the label. */
.info-tip {
    display: contents;
}

.info-tip-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    /* a 32px tap area around a 16px icon, without moving the label */
    width: 32px;
    height: 32px;
    margin: -8px -8px -8px -4px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: var(--bs-secondary-color, #6c757d);
    cursor: pointer;
}

.info-tip-button:hover,
.info-tip-button[aria-expanded="true"] {
    color: var(--bs-primary, #0d6efd);
}

.info-tip-button:focus-visible {
    outline: 2px solid var(--bs-primary, #0d6efd);
    outline-offset: -4px;
}

/* On a line of its own between the label and the box, pushing the box down while open: floating
   over the box, it hid the very field it explains (the loan rate). Being in the label row, it never
   runs off a phone screen however long the label is. */
.info-tip-bubble {
    flex-basis: 100%;
    max-width: 280px;
    margin-top: 0.25rem;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    background: #212529;
    color: #fff;
    font-size: 0.875rem;
    line-height: 1.4;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
}
</style>
